/**
 * Super Admin · Master Rule Library - FOCTTA-curated canonical rules.
 *
 * Platform-ops authors rules once, distributes to tenants on opt-in or
 * auto-push. Each curated rule shows its Rego excerpt, regulatory
 * grounding, industry anchors, and current distribution count.
 */
import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { Library, GitBranch, Send } from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { listMasterRules, promoteMasterRule, type CuratedRule } from '@/lib/api';

const DEMO_TENANT_ID = '11111111-1111-1111-1111-111111111111';

export function SuperAdminMasterRuleLibrary(): JSX.Element {
  const [rules, setRules] = useState<CuratedRule[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [promoting, setPromoting] = useState<string | null>(null);
  const [lastPromotion, setLastPromotion] = useState<{ ruleId: string; count: number } | null>(null);

  useEffect(() => {
    listMasterRules()
      .then((r) => setRules(r.rules))
      .catch((e) => setError(e instanceof Error ? e.message : 'unknown'));
  }, []);

  async function handlePromote(ruleId: string): Promise<void> {
    setPromoting(ruleId);
    setError(null);
    try {
      const result = await promoteMasterRule(ruleId, DEMO_TENANT_ID);
      setLastPromotion({ ruleId: result.ruleId, count: result.distributionCount });
      // Update local distribution count
      setRules((prev) => prev?.map((r) => r.ruleId === ruleId ? { ...r, distributionCount: result.distributionCount } : r) ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'promotion failed');
    } finally {
      setPromoting(null);
    }
  }

  return (
    <PageContainer>
      <PageHeader
        title="Super Admin · Master Rule Library"
        subtitle="FOCTTA-curated canonical rules - authored once, distributed to tenants. Each rule maps to a real Indian or international regulation; the OPA Rego body is the actual policy that runs in the Decision Gateway."
        badges={[
          { label: 'Platform-curated', variant: 'glass' },
          { label: rules ? `${rules.length} rules` : '-', variant: 'info' },
        ]}
      />

      {error && (
        <GlassCard className="p-4 mb-4 border-destructive">
          <p className="text-xs text-destructive">{error} - this page requires platform_super_admin / platform_ops actor role.</p>
        </GlassCard>
      )}

      {lastPromotion && (
        <GlassCard className="p-4 mb-4 border-primary glow-low">
          <p className="text-xs text-foreground">
            ✓ Rule <code className="font-mono">{lastPromotion.ruleId}</code> promoted to IndiaPay Capital. Distribution count now: <span className="font-display tabular">{lastPromotion.count}</span> tenants.
          </p>
        </GlassCard>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {rules?.map((r, i) => (
          <motion.div key={r.ruleId} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
            <GlassCard className="p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Library className="h-4 w-4 text-primary" />
                  <Badge variant={r.severity === 'critical' ? 'critical' : r.severity === 'high' ? 'warning' : 'glass'} className="text-[9px] uppercase">{r.severity}</Badge>
                  <Badge variant="info" className="text-[9px]">{r.category}</Badge>
                </div>
                <Badge variant="glass" className="text-[9px]">{r.distributionCount} tenants</Badge>
              </div>

              <CardTitle className="text-sm leading-tight">{r.name}</CardTitle>
              <code className="text-[10px] font-mono opacity-70">{r.ruleId}</code>

              <p className="text-xs text-muted-foreground mt-3 leading-relaxed">{r.rationale}</p>

              <div className="mt-3 flex flex-wrap gap-1">
                {r.industryAnchors.map((a) => (
                  <Badge key={a} variant="glass" className="text-[9px]">{a === '*' ? 'all industries' : a.replace(/_/g, ' ')}</Badge>
                ))}
              </div>

              <details className="mt-3 group">
                <summary className="text-[10px] uppercase tracking-wider text-muted-foreground cursor-pointer hover:text-foreground">
                  Rego body
                </summary>
                <pre className="text-[10px] font-mono opacity-80 mt-2 p-2 glass-lite rounded-md overflow-x-auto whitespace-pre-wrap">{r.regoBodyExcerpt}</pre>
              </details>

              <Button
                size="sm"
                variant="glass"
                className="w-full mt-3"
                onClick={() => handlePromote(r.ruleId)}
                disabled={promoting !== null}
              >
                <Send className="h-3 w-3" /> {promoting === r.ruleId ? 'Promoting…' : 'Promote to IndiaPay Capital'}
              </Button>
            </GlassCard>
          </motion.div>
        ))}
      </div>
    </PageContainer>
  );
}
